import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDemo } from "./useDemo";
import { useTour } from "../tour/tourContext";
import { DEMO_CHAPTERS } from "./demoTour";
import { chapterMatches, chapterRoutes } from "../tour/chapters";
import { useI18n, type TranslationKey } from "../i18n";
import "./DemoBanner.css";

/** The five stages the user walks through, and which chapters belong to each.
 *  Chapter numbers come from DEMO_CHAPTERS (0 is the welcome card). */
const STAGES: Array<{ key: TranslationKey; chapters: number[] }> = [
  { key: "tour.stages.site", chapters: [1] },
  { key: "tour.stages.verify", chapters: [2] },
  { key: "tour.stages.scan", chapters: [3, 4] },
  { key: "tour.stages.finding", chapters: [5] },
  { key: "tour.stages.report", chapters: [6] },
];

function stageOf(chapter: number): number {
  const i = STAGES.findIndex((s) => s.chapters.includes(chapter));
  return i < 0 ? 0 : i;
}

/** Strip above the app while the demo runs: where you are in the tour, and
 *  the way out. */
export function DemoBanner() {
  const { active, chapter } = useDemo();
  const { exitDemo } = useTour();
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  // While the demo runs, tell the page how tall this sticky strip is so the
  // sticky sidebar and chat sit below it instead of sliding underneath (that
  // clipped the chat and its tour highlight).
  useEffect(() => {
    const body = document.body;
    if (!active) return;
    body.classList.add("demo-on");
    const el = ref.current;
    const apply = () => body.style.setProperty("--demo-banner-h", `${el?.offsetHeight ?? 34}px`);
    apply();
    const ro = typeof ResizeObserver !== "undefined" && el ? new ResizeObserver(apply) : null;
    if (ro && el) ro.observe(el);
    return () => {
      ro?.disconnect();
      body.classList.remove("demo-on");
      body.style.removeProperty("--demo-banner-h");
    };
  }, [active]);

  if (!active) return null;

  // Off the chapter's screens the tour is silent — offer the way back.
  const current = DEMO_CHAPTERS[chapter];
  const offRoute = !!current && !chapterMatches(current.route, pathname);
  const resume = current ? current.resume ?? chapterRoutes(current.route)[0] : "/overview";
  const at = stageOf(chapter);

  return (
    <div className="demo-banner" role="status" ref={ref}>
      <span className="demo-banner__label">
        <span className="demo-banner__dot" aria-hidden />
        {t("tour.bannerShort")}
      </span>

      <ol className="demo-steps" aria-label={t("tour.bannerShort")}>
        {STAGES.map((s, i) => {
          const state = i < at ? "done" : i === at ? "now" : "next";
          return (
            <li key={s.key} className={`demo-step demo-step--${state}`} aria-current={state === "now" ? "step" : undefined}>
              <span className="demo-step__mark" aria-hidden>
                {state === "done" ? "✓" : i + 1}
              </span>
              <span className="demo-step__label">{t(s.key)}</span>
              {i < STAGES.length - 1 ? <span className="demo-step__line" aria-hidden /> : null}
            </li>
          );
        })}
      </ol>

      <span className="demo-banner__actions">
        {offRoute ? (
          <button type="button" className="demo-banner__link" onClick={() => navigate(resume)}>
            {t("tour.bannerBack")}
          </button>
        ) : null}
        <button type="button" className="demo-banner__link" onClick={exitDemo}>
          {t("tour.bannerExit")}
        </button>
      </span>
    </div>
  );
}
